/**
 * CONTRACT.md §2.6 — status. **Three states, and that is the whole list.**
 *
 * `PillState` is the enforcement: `need` (this needs your decision, or it costs
 * you more), `bad` (genuinely broken), and nothing — routine, the default. §1:
 * "'Good' / 'paid' / 'delivered' / 'on track' is **not a state**. It has no
 * colour and usually no chip at all — the sentence already says it." §3 records
 * `.pill--jade`, `.pill--steel`, `.pill--muted`, `.pill--neutral` and
 * `.pill--plain` as removed, so a fourth value here is not an omission.
 *
 * `Tag` is a *categorical* label — a service, a channel, a file type. It is not
 * a state and takes no state.
 */

import type { ReactNode } from 'react'

import { cx, type Common } from './common.js'

/** Ochre, red, or the routine default. There is no fourth. */
export type PillState = 'need' | 'bad'

export type PillProps = Common & {
  readonly state?: PillState | undefined
  readonly children: ReactNode
}

/** Dot + word. The dot is `::before`; nothing renders it. */
export function Pill({ state, children, ...common }: PillProps) {
  return (
    <span
      className={cx('pill', state === 'need' && 'pill--need', state === 'bad' && 'pill--bad')}
      {...common}
    >
      {children}
    </span>
  )
}

export type TagProps = Common & { readonly children: ReactNode }

export function Tag({ children, ...common }: TagProps) {
  return (
    <span className="tag" {...common}>
      {children}
    </span>
  )
}

export type NoticeProps = Common & {
  /** `.notice--need` — the same strip in ochre. */
  readonly need?: boolean | undefined
  /** `status` for something that just happened, `alert` for a refusal. */
  readonly role?: 'status' | 'alert' | undefined
  readonly children: ReactNode
}

/**
 * The left-ruled strip. Two states, because §3 removed `.notice--jade` ("a
 * good-news strip is just a sentence") and `.notice--bad` ("a failed line is a
 * `pill--bad` chip and a `tr.is-bad` row").
 */
export function Notice({ need, role, children, ...common }: NoticeProps) {
  return (
    <div
      className={cx('notice', need && 'notice--need')}
      role={role}
      aria-live={role === 'status' ? 'polite' : undefined}
      {...common}
    >
      {children}
    </div>
  )
}

export type NeedsItemProps = Common & { readonly children: ReactNode }

/**
 * A row in the "waiting on you" list. §3 removed the `.needs` wrapper: these go
 * straight into a `CardBody tight`, and the trailing `ButtonRow` is pushed
 * right by app.css rather than by a `Push`.
 */
export function NeedsItem({ children, ...common }: NeedsItemProps) {
  return (
    <div className="needs__item" {...common}>
      {children}
    </div>
  )
}

/** The 3px ochre rail on that row. The only list allowed one. */
export function NeedsBar() {
  return <span className="needs__bar" aria-hidden="true" />
}
